import {
  EMPTY_OTHER_WORKSPACES,
  type OtherWorkspacesOverview,
  WORKSPACE_LINK_PARAM,
} from '@fos/shared/contracts/workspaces-overview';
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { errorMessage } from '../../lib/api';
import { useActiveSession, useSwitchWorkspace } from '../../lib/auth';
import { trpc } from '../../lib/trpc';
import { toast } from '../../ui/Toast';

/**
 * „Masz coś w innym workspace'ie” (DECISIONS 2026-10-08): dane dla licznika przy przełączniku workspace'ów, sekcji
 * „W innych workspace'ach” na Dziś i w Czasie oraz obsługa linków z innego workspace'u (`?ws=<slug>`).
 * Przegląd (`identity.otherWorkspaces`) jest pobierany tylko, gdy osoba ma więcej niż jeden workspace.
 */

const MY_WORKSPACES_STALE_MS = 5 * 60_000;
const OTHER_WORKSPACES_STALE_MS = 60_000;
const OTHER_WORKSPACES_REFRESH_MS = 2 * 60_000;

export function useMyWorkspaces() {
  return trpc.identity.listMyWorkspaces.useQuery(undefined, { staleTime: MY_WORKSPACES_STALE_MS });
}

export function useOtherWorkspaces(): { data: OtherWorkspacesOverview; multi: boolean; isPending: boolean } {
  const mine = useMyWorkspaces();
  const multi = (mine.data?.length ?? 0) > 1;
  const q = trpc.identity.otherWorkspaces.useQuery(undefined, {
    enabled: multi,
    staleTime: OTHER_WORKSPACES_STALE_MS,
    refetchInterval: OTHER_WORKSPACES_REFRESH_MS,
  });
  return { data: q.data ?? EMPTY_OTHER_WORKSPACES, multi, isPending: multi && q.isPending };
}

/**
 * Komunikat po przełączeniu workspace'u bez menu (klik w pozycję z innego workspace'u, link `?ws=`): karta zadania
 * wygląda tak samo w każdym workspace'ie, więc mówimy wprost, gdzie osoba jest (Przegląd Fali 11 — ryzyko wpisania
 * czasu w złym workspace'ie).
 */
export function switchedToast(name: string | null | undefined): void {
  if (name) toast.info(`Przełączono na: ${name}`);
}

/** Przełącza workspace (istniejące `POST /api/auth/workspace`) i otwiera ścieżkę w nim. */
export function useOpenInWorkspace(): (workspaceId: string, path: string) => void {
  const session = useActiveSession();
  const switchWorkspace = useSwitchWorkspace();
  const navigate = useNavigate();
  const mine = useMyWorkspaces();
  return (workspaceId, path) => {
    if (workspaceId === session.workspace?.id) {
      navigate(path);
      return;
    }
    const name = mine.data?.find((w) => w.id === workspaceId)?.name;
    switchWorkspace.mutateAsync(workspaceId).then(
      () => {
        navigate(path);
        switchedToast(name);
      },
      (err: unknown) => toast.error(errorMessage(err)),
    );
  };
}

function withoutWorkspaceParam(pathname: string, search: string, hash: string): string {
  const params = new URLSearchParams(search);
  params.delete(WORKSPACE_LINK_PARAM);
  const rest = params.toString();
  return `${pathname}${rest ? `?${rest}` : ''}${hash}`;
}

/**
 * Link do innego workspace'u (push, brief, ścieżka z innego workspace'u): `?ws=<slug>`. Osoba jest członkiem →
 * przełączenie i ta sama ścieżka bez parametru; nie jest → zwykły brak dostępu (Dziś + komunikat, bez nazwy
 * workspace'u). Zwraca `true`, dopóki trwa rozstrzyganie — powłoka nie renderuje wtedy strony (rekord z innego
 * workspace'u w bieżącym dałby „nie znaleziono”).
 */
export function useWorkspaceLinkGate(): boolean {
  const location = useLocation();
  const navigate = useNavigate();
  const session = useActiveSession();
  const switchWorkspace = useSwitchWorkspace();
  const slug = new URLSearchParams(location.search).get(WORKSPACE_LINK_PARAM);
  const current = session.workspace?.slug ?? null;
  const mine = trpc.identity.listMyWorkspaces.useQuery(undefined, {
    staleTime: MY_WORKSPACES_STALE_MS,
    enabled: Boolean(slug) && slug !== current,
  });
  const handled = useRef<string | null>(null);
  const target = withoutWorkspaceParam(location.pathname, location.search, location.hash);

  useEffect(() => {
    if (!slug) {
      handled.current = null;
      return;
    }
    if (slug === current) {
      navigate(target, { replace: true });
      return;
    }
    if (handled.current === slug) return;
    const deny = () => {
      handled.current = slug;
      toast.error('Brak dostępu do tej strony.');
      navigate('/dzis', { replace: true });
    };
    if (mine.isError) return deny();
    if (!mine.data) return;
    const ws = mine.data.find((w) => w.slug === slug);
    if (!ws) return deny();
    handled.current = slug;
    switchWorkspace.mutateAsync(ws.id).then(
      () => {
        navigate(target, { replace: true });
        switchedToast(ws.name);
      },
      () => deny(),
    );
  }, [slug, current, mine.data, mine.isError, navigate, switchWorkspace, target]);

  return Boolean(slug) && slug !== current;
}
